/**
 * 악덕알리미 공용 렌더 도구 — 리스트/내제보/마스터상세가 공유하는 프리미티브·컴포넌트.
 *
 *  $j.F.Devil.Render.{esc, won, typeBadge, typeAccent, ownerTag}      // 프리미티브
 *                   .{reporterCard, callList}                          // 컴포넌트
 *                   .{masterTitle, masterBody, renderMasterInto, masterTarget} // 마스터 상세
 *
 * 데이터/마스킹은 서버(DvMask)가 강제하므로 여기선 표시만 한다.
 * (Pages 번들 b.js / F 번들 main.js 양쪽에서 런타임 `$j.F.Devil.Render` 로 호출 — 중복 번들 없음)
 */
// 타입 → 상태 배지 클래스 (체불/심사중/정정/임시/반려/회수)
const BADGE = { '체불': 'b-badge--r', '심사중': 'b-badge--sub', '정정': 'b-badge--sm', '임시': 'b-badge--d', '반려': 'b-badge--rj', '회수': 'b-badge--c', '일부회수': 'b-badge--pc', '해제': 'b-badge--d' };
// 타입 → 리스트 카드 좌측 강조선 (명시 없으면 기본 레드)
const ACCENT = { '회수': 'b-card--accent-b', '정정': 'b-card--accent-sm', '반려': 'b-card--accent-o', '임시': '', '해제': '' };

// 전화 아이콘 — 통화 공유 표식(작게, currentColor) / 제보자 통화 버튼(흰색)
const ICON_PATH = 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z';
const SHARE_ICON = `<svg class="b-call__share-ico" width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="${ICON_PATH}" stroke="currentColor" stroke-width="2"/></svg>`;
const CALL_ICON  = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="${ICON_PATH}" stroke="var(--on-brand)" stroke-width="2"/></svg>`;   // --C 버튼 위 — 다크는 어두운 획
// 다중 전화 칩 색상 — 번호끼리 구분만 하면 되므로 devil 포인트 토큰 6개를 순환한다.
// (구 팔레트는 tailwind hex 하드코딩이라 다크모드에서 그대로 남아 카드와 어긋났다)
const PHONE_CHIP_COLORS = ['var(--B)', 'var(--C)', 'var(--V)', 'var(--O)', 'var(--A)', 'var(--R)'];

export default class Render {
    /* ── 프리미티브 ── */
    esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    /**
     * 금액 → "숫자+단위" 한 덩어리 HTML(.u-nowrap) — 확대에서 "원"만 다음 줄로 밀리지 않게. HTML 이다: 템플릿 안에서만 쓴다.
     *   won(1200000)                                   → <span class="u-nowrap u-amt">1,200,000<span class="u-amt__unit">원</span></span>
     *   won(70500000000, { compact: true })            → 705.0억원 (억·만 축약)
     *   won(n, { compact: true, cls: 'b-xxx' })         → 바깥 span 에 클래스 추가
     */
    won = (n, opts = {}) => {
        const v = Math.trunc(Number(n) || 0);
        const comma = (x) => String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        let num = comma(v), unit = '원';
        if (opts.compact && Math.abs(v) >= 100000000) { num = comma((v / 100000000).toFixed(1)); unit = '억원'; }
        else if (opts.compact && Math.abs(v) >= 10000) { num = comma(Math.trunc(v / 10000)); unit = '만원'; }
        const cls = 'u-nowrap u-amt' + (opts.cls ? ' ' + opts.cls : '');
        return `<span class="${cls}">${num}<span class="u-amt__unit">${unit}</span></span>`;
    };
    typeBadge = (type) => BADGE[type] || 'b-badge--r';
    typeAccent = (type) => (Object.prototype.hasOwnProperty.call(ACCENT, type) ? ACCENT[type] : 'b-card--accent');
    ownerTag = (isMine) => (isMine
        ? '<span class="b-tag b-tag--mine">내 제보</span>'
        : '<span class="b-tag b-tag--other">타인</span>');

    /* ── 홈 알림 카드 (개인 푸시 수신함 — DvHomeService::myAlerts) ──
     * a: {kind, mod, co, phone, amount, date, master_num, report_num, mine, regtype}
     * 윗줄=배지(등록이면 신규/추가제보, 그 외 종류 kind) + 업체명,
     * 아랫줄=일시(배지 아래 가운데) · 전화(업체명 아래) · 끝에 금액(만원 아래는 버림 — 정확한 값은 상세).
     * master_num 있으면 클릭 시 악덕 상세 BS(Home.bindAlerts).
     */
    alertCard = (a) => {
        const esc = this.esc;
        const masterAttr = a.master_num ? ` data-master-num="${a.master_num}"` : '';
        const reportAttr = a.report_num ? ` data-report-num="${a.report_num}"` : '';
        const amount = a.amount ? this.won(a.amount, { compact: true, cls: 'b-alert__amt' }) : '';
        // 배지 — 등록 알림(신규/추가제보)은 종류(kind) 대신 신규/추가를 노출.
        const label = a.regtype === 'new' ? '신규제보'
                         : a.regtype === 'add' ? '추가제보' : esc(a.kind);
        return `
        <div class="b-alert"${masterAttr}${reportAttr}>
            <span class="b-alert__badge b-alert__badge--${esc(a.mod)}">${label}</span>
            <p class="b-alert__co">${esc(a.co)}</p>
            <span class="b-alert__meta b-alert__date">${esc(a.date)}</span>
            <span class="b-alert__meta">${esc(a.phone)}</span>
            ${amount}
        </div>`;
    };

    /* ── 제보자 카드 (3단: 헤더 ID·장비·뱃지·제보일 / 중단 업체·금액 / 하단 작업·피해) ──
     * @param r       제보 데이터 (masterDetailView reports[])
     * @param master  마스터(co_name/biz_no/ceo_name/address) — 업체정보 표시용(서버 마스킹 반영)
     *
     * 제보자 통화 버튼(안심번호)은 2026-08 QA피드백부터 내 제보/타인 제보 구분 없이 항상 노출한다
     * (자기 자신에게 거는 통화는 서버가 별도로 막는다 — DvCallService::connectToReporter).
     */
    /* opts.showCall — 이 업체에 내 제보가 있을 때만 true(_reportsSwipe 가 m.is_mine 으로 넘김).
     * 2026-10-21 결정(기획자 철회·대표님 확인) — 제보자 [통화]는 같은 업체 제보자끼리만. 8/26 '무조건 노출' 되돌림.
     * 서버(DvCallService::connectToReporter)도 같은 조건을 다시 본다. */
    reporterCard = (r, master = {}, opts = {}) => {
        const esc = this.esc;
        const eq = r.equipment ? `<span class="b-badge b-badge--eq">${esc(r.equipment)}</span>` : '';
        const firstTag = r.is_first ? '<span class="b-rc__tag b-rc__tag--first">최초제보</span>' : '';
        const recTag = r.recovered ? '<span class="b-rc__tag b-rc__tag--rec">회수완료</span>' : '';
        // 2026-09-01 상필 님 QA — "대표자 OOO"처럼 업체명 앞에도 "업체명" 라벨을 붙여
        // 일관되게(라벨 없이 값만 있으면 사람이름인지 업체명인지 한눈에 안 읽힘).
        const co = esc(r.co_name || master.co_name || '미입력');
        const subLine = [master.biz_no, master.person_name ? '체불자 ' + master.person_name : '', master.ceo_name ? '대표자 ' + master.ceo_name : ''].filter(Boolean).map(esc).join(' ');
        const addr = esc(master.address || '');
        // 2026-08 정책 변경 — 작업일·장소 한 줄 뭉침 해소(두 줄 분리) + 상세주소 포함, 신고내용(신고내역) 노출.
        const workDayRow = r.work_day ? `<p class="b-rc__work"><b>작업일</b> ${esc(r.work_day)}</p>` : '';
        const workLocRow = r.work_loc ? `<p class="b-rc__work"><b>작업지 주소</b> ${esc(r.work_loc)}</p>` : '';
        const descRow = r.damage_desc ? `<p class="b-rc__desc"><b>제보내용</b> ${esc(r.damage_desc)}</p>` : '';
        // 증빙 썸네일 — 서버가 URL 을 주는 건 내 제보건뿐(클릭 확대 dv-rc-photo). 남의 제보건은 장수(photo_cnt)만 와서
        // "증빙 N장 · 제보자만 열람" 한 줄. 예전엔 남의 건도 <img src> 로 그려 확대만 막았는데, 그러면 저장·전달을 못 막는다.
        const photos = Array.isArray(r.photos) ? r.photos : [];
        const photoCnt = photos.length || (parseInt(r.photo_cnt, 10) || 0);
        const thumbs = photos.length
            ? `<div class="b-rc__thumbs">${photos.map((u) =>
                `<img class="b-rc__thumb dv-rc-photo" data-src="${esc(u)}" src="${esc(u)}" alt="증빙">`).join('')}</div>`
            : (photoCnt ? `<p class="b-rc__work"><b>증빙</b> ${photoCnt}장 · 제보자만 열람</p>` : '');
        const foot = (workDayRow || workLocRow || descRow || thumbs)
            ? `<div class="b-rc__foot">${workDayRow}${workLocRow}${descRow}${thumbs}</div>` : '';
        // 2026-08 기획자 피드백 — 제보자 이름을 카드 최상단(헤더)에 두면 "제보자가 곧 악덕업체"로
        // 오인될 수 있어, 이름은 통화 버튼과 같은 줄(하단)로 내림. 최초제보/회수완료·제보일은
        // 사람 이름이 아니라 오인 소지가 없으므로 헤더에 그대로 둔다.
        // 다만 이름만 덩그러니 있으면 누구인지 안 읽혀서(2026-08 QA 확인) "제보자" 라벨을 붙인다.
        // 통화 버튼은 이 업체에 내 제보가 있을 때(opts.showCall), 남의 제보 카드에만(2026-10-21 결정 — 같은 업체
        // 제보자끼리만. 8/26 '무조건 노출' 철회). 내 제보 카드엔 본인 연결이라 안 그린다(서버도 막음).
        const name = esc(r.reporter_id || r.reporter || '제보자');
        const callBtn = (opts.showCall && !r.is_mine)
            ? `<button type="button" class="b-rc__call" data-report="${r.num || ''}" data-name="${name}" aria-label="제보자에게 전화">${CALL_ICON}<span class="b-rc__call-label">통화</span></button>` : '';
        const call = `<div class="b-rc__call-wrap"><span class="b-rc__id"><span class="b-rc__id-label">제보자</span>${name}</span>${callBtn}</div>`;

        return `
        <div class="b-card b-rc ${r.is_mine ? 'b-rc--self' : ''}">
            <div class="b-rc__head">
                <span class="b-rc__tags">${eq}${firstTag}${recTag}</span>
                <span class="b-rc__date">제보일 ${esc(r.date || '')}</span>
            </div>
            <div class="b-rc__mid">
                <span class="b-rc__co">${co}</span>
                <span class="b-rc__amt ${r.recovered ? 'is-rec' : ''}">${this.won(r.amount)}${r.recovered ? ' ✓' : ''}</span>
            </div>
            ${subLine ? `<p class="b-rc__sub">${subLine}</p>` : ''}
            ${addr ? `<p class="b-rc__sub">${addr}</p>` : ''}
            ${(Array.isArray(r.extra_phones) && r.extra_phones.length)
                ? `<div style="display:flex;flex-wrap:wrap;align-items:center;gap:12px;margin-top:8px"><span style="font-size:12px;color:var(--t2);font-weight:600">추가번호</span>${r.extra_phones.map((p) => `<span style="display:inline-block;background:var(--sf2);color:var(--t2);border-radius:8px;padding:2px 9px;font-size:13px;font-weight:600">${esc(p)}</span>`).join('')}</div>`
                : ''}
            ${foot}
            ${call}
        </div>`;
    };

    /* ── 통화목록 (b-call). opts: {locked} 비공유/미열람 시 흐림, {callable} 내 제보가 있는 업체인지(기본 true) ──
       2026-09-11 기획 — 내 제보가 아닌 업체의 통화목록은 "공유" 표시는 하되 전화는 안 되게. 그래서 callable 이 아니면
       칩을 <button> 이 아니라 <span class="is-off"> 으로 그리고 전화 아이콘도 뺀다(클릭 핸들러는 button.is-shared:not(.is-off) 만 받는다).
       서버(DvCallService::connectToSharedCall)도 같은 조건을 다시 본다. */
    callList = (calls = [], opts = {}) => {
        const callable = opts.callable !== false;
        const esc = this.esc;
        let inner;
        if (!calls.length) {
            inner = '<p class="b-detail__empty">통화 기록이 없습니다.</p>';
        } else {
            inner = calls.map((c) => {
                // "공유" 칩 클릭 시 안심번호 연결 확인 모달(renderMasterInto 의 _openConnectModal
                //   공용 핸들러) → 전화 걸기 시 가상번호로 발신.
                //   <a href="javascript:void(0)"> 로 만들었을 때 무반응(2026-08 QA, 실기기 재현·
                //   앱 완전 재시작 후에도 재현 — 캐시 문제 아님)이 보고돼 <button type="button">
                //   으로 교체했다 — 이미 같은 페이지에서 문제없이 동작하는 제보자 통화 버튼
                //   (.b-rc__call)도 <button>이라 이 쪽만 <a>였던 게 유일한 구조적 차이였음.
                //   (2026-08) data-real-number(실번호)를 DOM에 심던 걸 data-call-id(통화기록 ID)로
                //   교체 — 실번호는 서버 응답(buildCallList)에서도 이제 아예 안 보낸다. 서버가
                //   ID로 내부 조회해 안심번호만 돌려준다(DvCallService::connectToSharedCall).
                const shareLabel = c.is_shared ? (SHARE_ICON + '공유') : '공유안함';
                const shareHtml = !c.is_shared
                    ? `<span class="b-call__share">${shareLabel}</span>`
                    : callable
                        ? `<button type="button" class="b-call__share is-shared" data-caller="${esc(c.caller)}" data-call-id="${c.id}">${shareLabel}</button>`
                        : `<span class="b-call__share is-shared is-off" aria-disabled="true" title="내 제보가 있는 업체만 연결할 수 있습니다">공유</span>`;
                return `
                <div class="b-call">
                    <span class="b-call__caller">${esc(c.caller)}</span>
                    <span class="b-call__time">${esc(c.call_time || '')}</span>
                    ${shareHtml}
                </div>`;
            }).join('');
        }
        return `<div class="b-card ${opts.locked ? 'b-calls--locked' : ''}">${inner}</div>`;
    };

    /* ── 마스터 상세 ── */
    // 헤더 타이틀 (배지 + 마스킹 전화)
    // 바텀시트 헤더 h2(블록)에 그대로 꽂히므로, 뱃지와 번호를 감싸 간격을 만든다.
    // 래퍼 없이 span 두 개를 이어붙이면 공백이 없어 서로 맞닿는다.
    masterTitle = (m = {}) => `<span class="b-detail__title"><span class="b-badge ${this.typeBadge(m.type)}">${this.esc(m.type)}</span><span class="b-detail__phone">${this.esc(m.phone || '')}</span></span>`;

    // ① 악덕 detail — 마스터 카드(업체명·체불액·사업자·대표·주소·전화)
    masterCard = (m = {}) => {
        const esc = this.esc;
        // 2026-08 QA피드백 — 대표자명/주소가 없으면(빈 값) "대표자"만 덩그러니 남거나 빈 줄이
        // 생기지 않도록 값 있을 때만 라벨을 붙인다(예전엔 '***' 가짜 값이 항상 채워져 안 드러났음).
        // 체불업자(개인) 이름(2026-10-12 기획 1-1)은 '체불자' 라벨로 대표자 앞에 — 리스트 카드 3줄째와 같은 순서
        const rep = [m.biz_no, m.person_name ? '체불자 ' + m.person_name : '', m.ceo_name ? '대표자 ' + m.ceo_name : ''].filter(Boolean).map(esc).join(' ');
        // 업체영역은 대표번호만 노출(헤더 타이틀에 이미 표시) — 추가번호는 제보영역에서 노출.
        // 2026-09-01 상필 님 QA — 업체명이 없으면(대표자 이름만 등록된 개인 채무자) 빈 줄
        // 대신 "업체명 없음"을 자연스럽게 표시(레거시 이관 시 이름만 있던 업체가 여기 해당).
        return `
        <section class="b-card b-detail__master">
            <div class="b-detail__master-top">
                <span class="b-detail__co">${esc(m.co_name) || '미입력'}</span>
                <span class="b-detail__amt">${this.won(m.amount)}</span>
            </div>
            ${rep ? `<p class="b-detail__rep">${rep}</p>` : ''}
            ${m.address ? `<p class="b-detail__addr">${esc(m.address)}</p>` : ''}
        </section>`;
    };

    // ② 제보자목록 섹션 — 헤더 + 스와이프 캐러셀 (추가번호는 각 제보 카드 안에서 표시)
    reportersSection = (reports = [], m = {}) =>
        `<div class="b-detail__sec-head"><p>제보자 목록</p><span>${reports.length}건</span></div>` + this._reportsSwipe(reports, m);

    // ④ 면책 문구 — 2026-08 기획자(유용현) 요청. 신고내용은 제보자 주장이며 사실 확인/보증 대상이
    // 아님을 상시 고지. 업체마다 달라지는 내용이 아니라 정적 문구라 서버 데이터 없이 하드코딩한다.
    // 위치는 masterBody 맨 아래(2026-08 기획자 최종 확인 — 잠금안내와는 역할이 달라 자리를 바꿈).
    disclaimerNote = () =>
        '<p class="b-detail__note b-detail__note--disclaimer">이 업체는 체불 내역이 제보된 업체입니다.<br>' +
        '제보 내용은 제보자의 주장이며 ㈜오다다가 사실 여부를 확인하거나 보증하지 않습니다.</p>';

    // ③ 통화목록 섹션 — 헤더 + 잠금 안내(미열람 시) + 목록
    // 잠금 안내("내 제보를 등록하면...")는 통화목록 헤더와 통화기록 카드 사이에 위치
    // (2026-08 기획자 최종 확인 — 면책 문구와는 별개 위치).
    // unlocked — 서버의 calls_unlocked 값(2026-08). is_mine 과 분리한 이유는 masterBody() 주석 참고.
    // callable — m.is_mine(내 제보가 있는 업체). calls_unlocked 는 QA2 에서 항상 true 라 전화 가능 여부까지 맡기면
    // 남의 업체에서도 전화가 걸렸다(2026-09-11 기획 지적). 표시(잠금 안내·흐림)는 calls_unlocked, 전화는 is_mine.
    callsSection = (calls = [], unlocked = false, callable = true) => {
        let html = `<div class="b-detail__sec-head"><p>통화목록</p><span>${calls.length}명</span></div>`;
        if (!unlocked) html += '<p class="b-detail__note">내 제보를 등록하면 통화목록을 이용할 수 있습니다</p>';
        html += this.callList(calls, { locked: !unlocked, callable });
        return html;
    };

    // 악덕 상세 본문 = ①악덕detail + ②제보자목록 swipe + ③통화목록 + ④면책 문구
    // (리스트 BS · 내제보 APPROVED/RECALLED 공용)
    masterBody = (res) => {
        const m = res.master || {};
        // 통화목록 잠금은 m.is_mine 이 아니라 m.calls_unlocked 로 판단한다 — is_mine 은 마스킹
        // (전화번호·대표자명·사업자번호) 기준이라 QA2 테스트 예외가 섞이면 마스킹까지 풀려버림
        // (2026-08 상필 님 QA). 서버가 둘을 분리해서 내려준다(DvQueryService::masterDetailView).
        // __view(세로 flex) + __stack(본문) 2겹 — 면책 문구를 시트 바닥에 붙이기 위한 골격.
        // BS 는 fixedHeight(95vh) 고정이라 제보 1건짜리 상세에선 본문 아래로 100px 넘는 빈 칸이
        // 남았다. 자세한 사정은 pages/_detail.scss 의 __view 주석.
        return '<div class="b-detail__view"><div class="b-detail__stack">'
             + this.masterCard(m)
             + this.reportersSection(res.reports || [], m)
             + this.callsSection(res.calls || [], !!m.calls_unlocked, !!m.is_mine)
             + '</div>'
             + this.disclaimerNote()
             + '</div>';
    };

    // 제보자 카드를 좌우 스와이프 캐러셀로 묶는다 (1건이면 화살표/dot 미표시)
    // 1건일 땐 b-swipe--peek(다음 카드 살짝 보이기, 카드폭 -16px)도 붙이지 않는다 —
    // 안 그러면 넘길 카드가 없는데도 카드만 16px 짧게 끝나 업체정보·통화목록 카드와
    // 우측 경계가 안 맞아 보인다(2026-08 최실장·양은정 님 지적, "말풍선 크기" 문의).
    _reportsSwipe = (reports, m) => {
        if (!reports.length) return '<div class="b-card"><p class="b-detail__empty">제보 내역이 없습니다.</p></div>';
        // 2026-10-21 결정 — 통화 버튼은 이 업체에 내 제보가 있을 때만(m.is_mine). 8/26 '항상 노출' 철회. reporterCard 참고.
        const showCall = !!m.is_mine;
        const cards = reports.map((r) => `<div class="b-swipe__card">${this.reporterCard(r, m, { showCall })}</div>`).join('');
        const multi = reports.length > 1;
        const arrows = multi ? `
            <button type="button" class="b-swipe__prev" aria-label="이전 제보자">
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6l4 4" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
            <button type="button" class="b-swipe__next" aria-label="다음 제보자">
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M4 2l4 4-4 4" stroke-width="2" stroke-linecap="round"/></svg>
            </button>` : '';
        const dots = multi
            ? `<div class="b-swipe__nav">${reports.map((_, i) => `<button type="button" class="b-swipe__dot ${i === 0 ? 'is-on' : ''}"></button>`).join('')}</div>` : '';
        return `
        <div class="b-rc-slider">
            <div class="b-swipe${multi ? ' b-swipe--peek' : ''}">
                <div class="b-swipe__track">${cards}</div>
                ${arrows}
            </div>
            ${dots}
        </div>`;
    };

    // 안심번호 연결 확인 모달 — 제보자 통화 버튼(.b-rc__call) · 통화목록 '공유' 칩(.b-call__share)
    // 공용. connectParam 은 /b/ajax/call/connect 로 그대로 보낼 파라미터
    // ({report_num} 또는 {real_number}). 실패 시 모달을 닫지 않고 안에 에러 문구만 띄운다
    // (이전엔 '공유' 칩이 팝업 없이 바로 발신하는 임시 코드였음 — 2026-08 상필 님 QA로 통일).
    _openConnectModal = (name, connectParam) => {
        const esc = this.esc;
        const svg = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2"/></svg>';
        const html = `
          <div style="text-align:center;padding:12px 2px 2px">
            <div style="width:76px;height:76px;border-radius:50%;background:var(--Cg);color:var(--C);display:flex;align-items:center;justify-content:center;margin:0 auto 16px">${svg}</div>
            <p style="font-size:22px;font-weight:800;color:var(--t1);margin:0 0 8px">${esc(name)}</p>
            <p style="font-size:14px;color:var(--t2);margin:0">상대방의 안심번호로 연결됩니다</p>
          </div>
          <button type="button" class="dv-callm-go" style="width:100%;height:54px;margin-top:24px;border:none;border-radius:14px;background:var(--C);color:var(--on-brand);font-size:16px;font-weight:800;display:flex;align-items:center;justify-content:center;gap:6px;cursor:pointer">${svg}<span>전화 걸기</span></button>
          <p class="dv-callm-err" style="display:none;color:var(--R);font-size:13px;text-align:center;margin:12px 0 0;line-height:1.5"></p>`;
        const m = $j.Ui.Modal.open({ title: '전화 연결', html, mode: 'center', variant: 'devil', width: '340px' });
        // 전화 걸기 → 공동제보자 검증(또는 실번호 매칭) 후 안심(가상)번호 발급 → 그 번호로 발신
        m.el.on('click', '.dv-callm-go', function () {
            const $go = $(this);
            const $err = m.el.find('.dv-callm-err');
            if ($go.data('busy')) return;
            $go.data('busy', true).css('opacity', .6);
            $err.hide().text('');
            const showErr = (msg) => {
                $err.text(msg || '안심번호 연결에 실패했습니다.').show();
                $go.data('busy', false).css('opacity', 1);
            };
            $.post('/b/ajax/call/connect', connectParam)
                .done((res) => {
                    if (res && res.ok && res.virtual_number) {
                        m.close();
                        location.href = 'tel:' + res.virtual_number;
                    } else {
                        showErr(res && res.message);
                    }
                })
                .fail((xhr) => showErr(xhr && xhr.responseJSON && xhr.responseJSON.message));
        });
    };

    // 컨테이너($body)에 마스터 상세 렌더 + 스와이프/통화버튼 와이어링 (단독 페이지·BS 공용)
    renderMasterInto = ($body, res) => {
        const openConnectModal = this._openConnectModal;
        $body.html(this.masterBody(res));
        // syncHeight — 제보자 카드는 내용 길이가 제각각이라, 트랙 높이를 현재 보이는 카드
        // 실제 높이로 맞춰야 짧은 카드 아래 빈 공간(캐러셀 자체 높이가 가장 긴 카드 기준으로
        // 고정되던 문제)이 안 남는다(2026-09, 상필 님 QA — Ui/Swipe.js 옵션 참고).
        $j.Ui.Swipe.init($body.find('.b-rc-slider'), { syncHeight: true });
        // 타제보자 전화 — BS 안에서 눌리므로 BS 중첩 금지 → 공용 Ui.Modal(sheet)로 BS 위에 스택.
        $body.off('click.bcall').on('click.bcall', '.b-rc__call', function (e) {
            e.stopPropagation();
            const name = $(this).data('name') || '제보자';
            const reportNum = $(this).data('report') || 0;
            openConnectModal(name, { report_num: reportNum });
        });
        // 통화목록 '공유' 칩 → 동일한 안심번호 연결 확인 모달 (2026-08 상필 님 QA —
        // 이전엔 팝업 없이 바로 발신하던 임시 코드였음). 서버로는 통화기록 ID만 보낸다 —
        // 실번호는 서버 내부에서만 조회(DvCallService::connectToSharedCall).
        // 위임 셀렉터라 태그를 안 가리면 내 제보 아님(span.is-off) 칩도 잡힌다 — button 에 한정 + is-off 제외 (2026-09-12 QA: 팝업이 그대로 떴음)
        $body.off('click.bshare').on('click.bshare', 'button.b-call__share.is-shared:not(.is-off)', function (e) {
            e.preventDefault();
            e.stopPropagation();
            const $chip = $(this);
            const name = $chip.data('caller') || '상대방';
            const callLogNum = $chip.data('callId') || 0;
            openConnectModal(name, { call_log_num: callLogNum });
        });
        // 증빙 썸네일 확대 — 같은 카드의 사진들을 갤러리로 (ZoomPhoto)
        $body.off('click.dvrcphoto').on('click.dvrcphoto', '.dv-rc-photo', function (e) {
            e.stopPropagation();
            if (!$j.Ui.ZoomPhoto) return;
            const $thumbs = $(this).closest('.b-rc').find('.dv-rc-photo');
            const items = $thumbs.map((_, el) => ({ url: $(el).data('src') })).get();
            if (items.length) $j.Ui.ZoomPhoto.open(null, { items, index: $thumbs.index(this) });
        });
    };

    // BS 타깃 — /b/ajax/master/{num} fetch 후 렌더 (List 카드 클릭·홈 알림·딥링크 공용)
    masterTarget = (masterNum) => {
        const self = this;
        return {
            init() {
                const $cont = $('#' + this.id);
                if (!masterNum) { $cont.html('<p class="b-detail__loading">잘못된 접근입니다.</p>'); return; }
                $cont.html('<p class="b-detail__loading">불러오는 중…</p>');
                this.parent.setTitle('업체 상세');
                // 세션이 끝나 로그인을 다녀와도 이어서 열 수 있게 기억(F/Devil/Master.js 머리말)
                const opener = $j.F.Devil.Master;
                opener.remember(masterNum, false);
                $.getJSON('/b/ajax/master/' + masterNum)
                    .done((res) => {
                        opener.settle();
                        if (!res || !res.result) {
                            $cont.html(`<p class="b-detail__loading">${self.esc((res && res.message) || '불러오기 실패')}</p>`);
                            return;
                        }
                        this.parent.setTitle(self.masterTitle(res.master || {}));
                        self.renderMasterInto($cont, res);
                    })
                    .fail((jqXHR) => {
                        opener.settle(jqXHR);
                        $cont.html('<p class="b-detail__loading">불러오기 실패</p>');
                    });
            },
            destroy() {},
        };
    };
}
